import { useEffect, useRef, useState } from 'react'
import type { MsgDeskPageProps } from '../../MsgDeskApp'
import { currentLegacyDeskMode } from '@/msgdeskRuntime/deskNavigation'
import { treeStore } from '@/msgdeskRuntime/state'
import {
  applyStandardMode,
  pushStandardModeUrl,
  standardModeFromPathname,
  type StandardRouteMode,
} from '../../host/deskRoutes'
import { PortalPage } from './PortalPage'
import { PortalSharedSurfaceProvider } from './Shared/SharedSurface'
import { PortalPanelsSurfaceProvider } from './panels/PanelsSurface'
import { PortalRowSurfaceProvider } from './rowTemplates/RowSurface'

function routeModeForProjection(mode: string): StandardRouteMode | undefined {
  if (mode === 'msg' || mode === 'answers' || mode === 'submsges') return mode
  return undefined
}

export function PortalRoutedPage({
  editorBridgeReady = false,
  initialMode = 'msg',
}: MsgDeskPageProps & { initialMode?: StandardRouteMode }) {
  const initialModeSettled = useRef(false)
  const initialModeAttempts = useRef(0)
  const [initialModeTick, setInitialModeTick] = useState(0)

  useEffect(() => {
    if (initialModeSettled.current || initialMode === 'msg') {
      initialModeSettled.current = true
      return
    }
    if (!editorBridgeReady) return

    const currentState = treeStore.getState()
    if (currentState.subsLoading) return
    const matchesRoute = currentLegacyDeskMode() === initialMode
    const timer = window.setTimeout(() => {
      if (matchesRoute) {
        initialModeSettled.current = true
        return
      }
      if (initialModeAttempts.current >= 100) return
      initialModeAttempts.current += 1
      applyStandardMode(initialMode)
      setInitialModeTick(value => value + 1)
    }, matchesRoute ? 300 : 100)

    return () => window.clearTimeout(timer)
  }, [editorBridgeReady, initialMode, initialModeTick])

  useEffect(() => treeStore.subscribe(() => {
    if (!initialModeSettled.current) return
    const mode = routeModeForProjection(treeStore.getState().renderProjection.mode)
    if (mode) pushStandardModeUrl(mode)
  }), [])

  useEffect(() => {
    const handlePopState = () => applyStandardMode(standardModeFromPathname(window.location.pathname))
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  return (
    <PortalSharedSurfaceProvider>
      <PortalRowSurfaceProvider>
        <PortalPanelsSurfaceProvider>
          <PortalPage />
        </PortalPanelsSurfaceProvider>
      </PortalRowSurfaceProvider>
    </PortalSharedSurfaceProvider>
  )
}
